import { useEffect } from 'react';
import { Box, Container, Grid, Typography, Divider } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import RecipeCard from '../../../components/RecipeCards/recipeCard';
import RecipeCardSkeleton from '../../../components/RecipeCards/recipeCardSkeleton';
import { getAllRecipes } from '../../../services/DbService';
import { straightFont, secondary } from '../../../Constants';
import IRecipe from '../../../interfaces/IRecipe';
import { useTheme } from '../../../providers/ThemeProvider';

export default function HomeBody() {
  const { colors } = useTheme();
  const queryClient = useQueryClient();

  const {
    data: recipesData,
    error: recipesError,
    isLoading,
  } = useQuery(['recipes'], async () => {
    const res = await getAllRecipes();
    return res;
  });

  // Pre-populate individual recipe cache entries from the list query
  // This eliminates Firestore reads when clicking into a recipe detail page
  useEffect(() => {
    if (recipesData) {
      recipesData.forEach((recipe) => {
        // Cache by ID
        queryClient.setQueryData(['recipe', recipe.id], recipe);
        // Also cache by slug if available
        if (recipe.slug) {
          queryClient.setQueryData(['recipe', recipe.slug], recipe);
        }
      });
    }
  }, [recipesData, queryClient]);

  if (recipesError) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">Error loading recipes</Typography>
      </Box>
    );
  }

  // Get featured recipes - sorted by engagement (favorites + ratings)
  const featuredRecipes = recipesData
    ? [...recipesData]
      .sort((a, b) => {
        const scoreA =
            (a.favorites || 0) * 2 + (a.averageRating || 0) * (a.commentsWithRatings || 0);
        const scoreB =
            (b.favorites || 0) * 2 + (b.averageRating || 0) * (b.commentsWithRatings || 0);
        return scoreB - scoreA;
      })
      .slice(0, 6)
    : [];

  return (
    <Box sx={{ backgroundColor: colors.background, py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
          <Typography
            variant="overline"
            sx={{
              color: secondary,
              fontFamily: straightFont,
              fontWeight: 600,
              fontSize: '0.85rem',
              letterSpacing: '2px',
            }}
          >
            Discover
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: straightFont,
              color: colors.text,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mt: 0.5,
            }}
          >
            Featured Recipes
          </Typography>
          <Divider
            sx={{
              width: 60,
              mx: 'auto',
              mt: 2,
              borderBottomWidth: 3,
              borderColor: secondary,
            }}
          />
        </Box>

        {/* Recipe Grid */}
        <Grid container spacing={3}>
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
              <Grid
                item
                key={index}
                xs={12}
                sm={6}
                md={4}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <RecipeCardSkeleton />
              </Grid>
            ))
            : featuredRecipes.map((recipe: IRecipe, index: number) => (
              <Grid
                item
                key={recipe.id}
                xs={12}
                sm={6}
                md={4}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true, amount: 0.2 }}
                  style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                >
                  <RecipeCard recipe={recipe} />
                </motion.div>
              </Grid>
            ))}
        </Grid>
      </Container>
    </Box>
  );
}
